import React from 'react';
import './BankSyncModal.css';
import './BankImportModal.css';

// Der FinTS-Umsatzabruf (FinTsClient::advance(), case 'accounts') holt immer
// die letzten 90 Tage - importiert man denselben Zeitraum zusaetzlich per
// CSV, erkennt der Dedup-Hash (TransactionStore.inc) das i.d.R. NICHT als
// Duplikat, weil MoneyMoney den Verwendungszweck-Text anders zusammensetzt
// als FinTS. Deshalb per Default nur aeltere Buchungen importieren - dann
// deckt CSV-Import die Altdaten ab und der folgende FinTS-Sync die letzten
// 90 Tage sauber, ganz ohne Ueberschneidung.
const FINTS_SYNC_WINDOW_DAYS = 90;

function ninetyDaysAgoIso() {
	const d = new Date();
	d.setDate(d.getDate() - FINTS_SYNC_WINDOW_DAYS);
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Import von MoneyMoney-CSV-Exporten (Datei -> Exportieren -> CSV in
// MoneyMoney). Zwei Schritte: Datei hochladen -> Vorschau (Backend parst nur,
// schreibt nichts) -> bestaetigen (Backend schreibt ueber dieselbe
// INSERT-IGNORE-Dedup-Logik wie der FinTS-Sync, siehe TransactionStore.inc).
export default function BankImportModal(props) {

	const x = props.x;
	const connections = (props.connections || []).filter((c) => parseInt(c.disabled, 10) !== 1);

	const [connectionId, setConnectionId] = React.useState(connections.length > 0 ? connections[0].connectionid : '');
	const [file, setFile] = React.useState(null);
	const [excludeRecent, setExcludeRecent] = React.useState(true);
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState('');
	const [transactions, setTransactions] = React.useState(null);
	const [excludedCount, setExcludedCount] = React.useState(0);
	const [importedCount, setImportedCount] = React.useState(null);

	function close() {
		props.onClose();
	}

	function analyze() {
		if (!file || connectionId === '') return;
		setBusy(true);
		setError('');
		const formData = new FormData();
		formData.append('csv', file, file.name);
		formData.append('connectionId', connectionId);
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingImportPreview', formData, (result) => {
			setBusy(false);
			if (result.data.returncode !== 1) {
				setError((result.data.content && result.data.content.message) || 'Datei konnte nicht gelesen werden.');
				return;
			}
			const all = result.data.content.transactions;
			if (excludeRecent) {
				const cutoff = ninetyDaysAgoIso();
				const filtered = all.filter((t) => t.bookingDate < cutoff);
				setExcludedCount(all.length - filtered.length);
				setTransactions(filtered);
			} else {
				setExcludedCount(0);
				setTransactions(all);
			}
		});
	}

	function confirmImport() {
		setBusy(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingImportConfirm', {
			connectionId: connectionId,
			transactions: JSON.stringify(transactions),
		}, (result) => {
			setBusy(false);
			if (result.data.returncode !== 1) {
				setError((result.data.content && result.data.content.message) || 'Import fehlgeschlagen.');
				return;
			}
			setImportedCount(result.data.content.importedCount);
		});
	}

	function finish() {
		props.onSuccess();
	}

	return (
		<div className="banking-sync-overlay">
			<div className="banking-sync-modal banking-import-modal">
				<div className="banking-sync-header">
					<span>Umsätze aus MoneyMoney importieren</span>
					<i className="bi bi-x-lg table-icon" onClick={close}></i>
				</div>

				{importedCount === null && transactions === null &&
					<div className="banking-sync-body">
						<label htmlFor="banking-import-connection">Zielkonto</label>
						<select
							id="banking-import-connection"
							value={connectionId}
							onChange={(e) => setConnectionId(parseInt(e.target.value, 10))}
						>
							{connections.map((c) => (
								<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
							))}
						</select>

						<label htmlFor="banking-import-file">CSV-Datei aus MoneyMoney</label>
						<input
							id="banking-import-file"
							type="file"
							accept=".csv,text/csv"
							onChange={(e) => setFile(e.target.files[0] || null)}
						/>
						<p className="banking-sync-hint">
							<i className="bi bi-info-circle"></i> MoneyMoney: Konto auswählen, Datei → Exportieren → CSV. Bereits vorhandene Buchungen werden automatisch übersprungen.
						</p>

						<label className="banking-sync-remember">
							<input
								type="checkbox"
								checked={excludeRecent}
								onChange={(e) => setExcludeRecent(e.target.checked)}
							/>
							Nur Buchungen älter als 90 Tage importieren (empfohlen)
						</label>
						{excludeRecent &&
							<p className="banking-sync-hint">
								<i className="bi bi-info-circle"></i> Die letzten 90 Tage holt der FinTS-Abgleich ohnehin automatisch – ein CSV-Import desselben Zeitraums würde dort nicht zuverlässig als Duplikat erkannt.
							</p>
						}

						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
						<button type="button" className="btn" disabled={busy || !file || connectionId === ''} onClick={analyze}>
							{busy ? 'Analysiere…' : 'Datei analysieren'}
						</button>
					</div>
				}

				{transactions !== null && importedCount === null && transactions.length === 0 &&
					<div className="banking-sync-body">
						<p className="banking-sync-hint">
							<i className="bi bi-info-circle"></i> Keine Buchungen älter als 90 Tage in der Datei gefunden ({excludedCount} ausgeschlossen). Die letzten 90 Tage holt der FinTS-Abgleich ohnehin automatisch.
						</p>
						<button type="button" className="btn" onClick={() => setTransactions(null)}>Zurück</button>
					</div>
				}

				{transactions !== null && importedCount === null && transactions.length > 0 &&
					<div className="banking-sync-body">
						<p>{transactions.length} Buchung{transactions.length === 1 ? '' : 'en'} werden importiert.</p>
						{excludedCount > 0 &&
							<p className="banking-sync-hint">
								<i className="bi bi-info-circle"></i> {excludedCount} Buchung{excludedCount === 1 ? '' : 'en'} der letzten 90 Tage ausgeschlossen – die holt der FinTS-Abgleich.
							</p>
						}
						<div className="banking-import-preview">
							{transactions.slice(0, 8).map((t, i) => (
								<div key={i} className="banking-import-preview-row">
									<span>{t.bookingDate}</span>
									<span>{t.counterpartyName}</span>
									<span className={parseFloat(t.amount) < 0 ? 'amount-negative' : 'amount-positive'}>{t.amount} €</span>
								</div>
							))}
							{transactions.length > 8 &&
								<p className="banking-sync-hint">… und {transactions.length - 8} weitere.</p>
							}
						</div>
						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
						<div className="banking-sync-all-actions">
							<button type="button" className="btn" disabled={busy} onClick={confirmImport}>
								{busy ? 'Importiere…' : transactions.length + ' Buchungen importieren'}
							</button>
							<button type="button" className="btn" disabled={busy} onClick={() => setTransactions(null)}>Zurück</button>
						</div>
					</div>
				}

				{importedCount !== null &&
					<div className="banking-sync-body">
						<p><i className="bi bi-check-circle-fill"></i> {importedCount} neue Buchung{importedCount === 1 ? '' : 'en'} importiert{transactions.length > importedCount ? ' (' + (transactions.length - importedCount) + ' bereits vorhanden, übersprungen)' : ''}.</p>
						<button type="button" className="btn" onClick={finish}>Fertig</button>
					</div>
				}
			</div>
		</div>
	)

};
